import Page from "design/pages/Page";
import React from "react";
import PageContainer from "design/layouts/PageContainer";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import useMyTasks from "backend/hooks/workflows/useMyTasks";
import MyTasksContainer from "design/pages/MyTasksContainer";
import MyTasksTable from "design/components/tables/MytasksTable";
import { t } from "i18next";
import Alert from "@material-ui/lab/Alert";
import Todo from "design/components/misc/Todo";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import useLink from "@qpr/client-api-base/hooks/useLink";
import { ICardElementWithChildren } from "backend/model/model";
import { useAppContext } from "@qpr/client-api-base/context/AppCtxProvider";
import MeasureTable from "design/components/tables/MeasureTable";
import useWorkflowInstance from "backend/hooks/workflows/useWorkflowInstance";
import useWorkflowLogs from "backend/hooks/workflows/useWorkflowLogs";
import useElement from "backend/hooks/elements/useElement";
import DashboardTitle from "design/components/texts/DashboardTitle";
import PeriodChip from "design/components/chips/PeriodChip";
import ElementLink from "components/link/ElementLink";

const CompletedTasks = () => {
  const link = useLink();
  const ctx = useAppContext();
  const { data: task, isLoading: isTaskLoading } = useWorkflowInstance(
    ctx.elementId
  );
  const { data: logs, isLoading: isLogsLoading } = useWorkflowLogs(task?.id);
  const { data: instanceElement } = useElement(task?.meaId, task?.period);
  const { data: myTasks, isLoading: isMyTasksLoading } =
    useMyTasks("completed");
  const selectedRow = React.useMemo(
    () =>
      myTasks
        ?.filter((item) => item.workflowState?.wfInstanceId === ctx.elementId)
        .pop(),
    [myTasks, ctx.elementId]
  );
  const selectRow = React.useCallback(
    (item: ICardElementWithChildren) => {
      link({ meaId: item.workflowState?.wfInstanceId });
    },
    [ctx.elementId, ctx.page]
  );
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };
  return (
    <Page isLoading={isMyTasksLoading || !myTasks} Toolbar={WorkflowToolbar}>
      <PageContainer width="100%">
        <SplitView>
          <MeasureTable
            keyFn={(item) => item?.workflowState.wfInstanceId || ""}
            items={myTasks || []}
            selectedRow={selectedRow}
            columns={[
              {
                title: t("completedtasks.State").toString(),
                value: (row) => (
                  <div>
                    <Alert severity="success">{t("completedtasks.OK")}</Alert>
                  </div>
                ),
                onRowClick: selectRow,
              },
              {
                title: t("workflowinput.table.Name").toString(),
                sortFn: (a, b) => a.name.localeCompare(b.name),
                onRowClick: selectRow,
                value: (item) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                      }}
                    >
                      <div>{item.elem.name}</div>
                      <div>
                        <i>{item.elem.scorecardname}</i>
                      </div>
                    </div>
                  );
                },
              },
              {
                title: t("completedtasks.ACT").toString(),
                value: (row) => <div>{row.elem.ACT_prettyvalue}</div>,
                onRowClick: selectRow,
              },
              {
                title: t("completedtasks.Period").toString(),
                onRowClick: selectRow,
                value: (row) => <div>{row.elem.workflowState?.period}</div>,
              },
              {
                title: t("completedtasks.Type").toString(),
                onRowClick: selectRow,
                value: (row) => <div>{row.elem.typename}</div>,
              },
              {
                title: t("completedtasks.Date").toString(),
                onRowClick: selectRow,
                sortFn: (a, b) => a.name.localeCompare(b.name),
                value: (row) => (
                  <div>
                    {formatDate(row.elem.workflowState?.startdate || "")}
                  </div>
                ),
              },
              {
                title: t("completedtasks.Status").toString(),
                onRowClick: selectRow,
                value: (row) => <div>{row.elem.STATUS}</div>,
              },
            ]}
          />
          <StackView padding="20px">
            {task && logs ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <DashboardTitle>
                    {instanceElement?.name}
                    <ElementLink item={instanceElement} />
                  </DashboardTitle>
                  <div style={{ width: "140px" }}>
                    <PeriodChip period={task?.period || ""} icon="KPI" />
                  </div>
                </div>
                {task.currentState === "approved" ? (
                  <Alert severity="success">This task has completed.</Alert>
                ) : (
                  <Alert severity="warning">
                    This task has not yet completed.
                  </Alert>
                )}
                <MeasureTable
                  items={logs || []}
                  columns={[
                    {
                      title: "User",
                      value: (row) => <div>{row.elem.username}</div>,
                    },
                    {
                      title: "Change",
                      value: (row) => <div>{row.elem.newstate}</div>,
                    },
                    {
                      title: "Date",
                      value: (row) => (
                        <div>
                          {new Date(row.elem.timestamp).toLocaleString()}
                        </div>
                      ),
                    },
                    {
                      title: "Message",
                      value: (row) => <div>{row.elem.message}</div>,
                    },
                  ]}
                />
              </>
            ) : null}
          </StackView>
        </SplitView>
      </PageContainer>
    </Page>
  );
};

export default CompletedTasks;
